<template>
  <section class="form-fw__content">
    <form
      v-if="status !== isFailed"
      class="form--custom"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <fieldset
        v-if="hasMoreThanOneCompany && hasMoreThanOneCostCenter"
        class="form__container"
        :class="{ 'form__container--first': isValidObject(company) }"
        :disabled="status === isLoading"
      >
        <header class="form__container__header">
          <h2 class="form__container__title">{{ $t(`${namespace}.containerCosteCenterTitle`) }}</h2>
          <p class="form__container__desc">{{ $t(`${namespace}.containerCosteCenterDesc`) }}</p>
        </header>

        <div class="form__container__content">
          <label>{{ $t(`${namespace}.fieldCompanyLabel`) }}*</label>
          <div class="form__group">
            <transition name="fade">
              <AutoComplete
                v-if="!isFetching"
                kind="company"
                :data="company"
                :placeholder="$t(`${namespace}.fieldCompanyPlaceholder`)"
                :isValid="isCompanyValid"
                required
                @input="updateCompany"
                @reset="resetFields(true, null, null)"
              />
            </transition>
          </div>

          <div v-if="isValidObject(company)">
            <div v-if="costCentersFetching">
              <div class="text-xs-center py-4" >
                <v-progress-circular color="accent" indeterminate />
              </div>
            </div>

            <div v-else>
              <div
                v-if="costCentersCounter <= 0"
                class="text-xs-center py-3"
              >
                <strong>{{ $t(`${namespace}.youDoNotHaveCostCenter`) }}</strong> {{ $t(`${namespace}.costCenterInfo`) }} <router-link :to="{ name: 'CostCenter', params: { type: 'cadastrar' } }" title="" class="accent--text">{{ $t('Common.clickHere') }}</router-link> {{ $t(`${namespace}.createYourFirst`) }} <strong>:D</strong>
              </div>

              <div v-else>
                <label>{{ $t(`${namespace}.fieldCostCenterLabel`) }}*</label>
                <AutoComplete
                  kind="costCenter"
                  :data="costCenters"
                  :placeholder="$t(`${namespace}.fieldCostCenterPlaceholder`)"
                  :disabled="!company"
                  multiple
                  :companyId="company ? company.id : null"
                  :isValid="isCostCenterValid"
                  required
                  @input="updateCostCenter"
                />
              </div>
            </div>
          </div>
        </div>
      </fieldset>

      <fieldset
        class="form__container"
        :disabled="status === isLoading || fieldsetDisabled"
      >
        <header class="form__container__header">
          <h2 class="form__container__title">{{ $t(`${namespace}.containerEmployeeTitle`) }}</h2>
          <p class="form__container__desc">{{ $t(`${namespace}.containerEmployeeDesc`) }}</p>
        </header>

        <div class="form__container__content">
          <label>{{ $t(`${namespace}.fieldEmployeeLabel`) }}*</label>
          <v-text-field
            v-model="form.employee.name"
            :placeholder="$t(`${namespace}.fieldEmployeePlaceholder`)"
            :error-messages="errors.collect($t(`${namespace}.fieldEmployeeName`))"
            v-validate="{ required: true }"
            :data-vv-name="$t(`${namespace}.fieldEmployeeName`)"
            required
            flat
            outline
            solo
            color="accent"
          />

          <v-layout row wrap>
            <v-flex sm6 xs12>
              <div class="pr-2--desktop">
                <label>{{ $t(`${namespace}.fieldEmailLabel`) }}*</label>
                <v-text-field
                  v-model="form.employee.email"
                  type="email"
                  :placeholder="$t(`${namespace}.fieldEmailPlaceholder`)"
                  :error-messages="errors.collect($t(`${namespace}.fieldEmailName`))"
                  v-validate="{ required: true, email: true }"
                  :data-vv-name="$t(`${namespace}.fieldEmailName`)"
                  required
                  flat
                  outline
                  solo
                  color="accent"
                />
              </div>
            </v-flex>

            <v-flex sm6 xs12>
              <div class="pl-2--desktop">
                <label>{{ $t(`${namespace}.fieldPhoneLabel`) }}*</label>
                <v-text-field
                  v-model="phone"
                  type="tel"
                  :placeholder="$t(`${namespace}.fieldPhonePlaceholder`)"
                  :error-messages="errors.collect($t(`${namespace}.fieldPhoneName`))"
                  v-validate="{ required: true, regex: /^\(\d{2}\)\s(\d{4,5})-(\d{4})$/ }"
                  :data-vv-name="$t(`${namespace}.fieldPhoneName`)"
                  required
                  flat
                  outline
                  solo
                  color="accent"
                  maxlength="15"
                  @input="setPhoneMask($event, 'phone')"
                />
              </div>
            </v-flex>
          </v-layout>

          <div v-if="!hasMoreThanOneCompany && hasMoreThanOneCostCenter">
            <label>{{ $t(`${namespace}.fieldCostCenterLabel`) }}*</label>
            <AutoComplete
              kind="costCenter"
              :data="costCenters"
              :placeholder="$t(`${namespace}.fieldCostCenterPlaceholder`)"
              :disabled="!company"
              multiple
              :companyId="company ? company.id : null"
              :isValid="isCostCenterValid"
              required
              @input="updateCostCenter"
            />
          </div>
        </div>
      </fieldset>

      <fieldset
        class="form__container form__container--collapse"
        :disabled="status === isLoading || fieldsetDisabled"
      >
        <header class="form__container__header" @click="collapse('complementInfoContainer')">
          <h2 class="form__container__title">{{ $t(`${namespace}.containerComplementTitle`) }}</h2>
          <p class="form__container__desc">{{ $t(`${namespace}.containerComplementDesc`) }}</p>
          <v-icon :color="complementInfoContainer ? 'accent' : 'gray-6'">
            {{ complementInfoContainer ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
          </v-icon>
        </header>

        <div v-if="complementInfoContainer" class="form__container__content pt-0">
          <label>{{ $t(`${namespace}.fieldSupervisorLabel`) }}</label>
          <AutoComplete
            v-if="!isFetching"
            kind="employee"
            :data="supervisor"
            :placeholder="$t(`${namespace}.fieldSupervisorPlaceholder`)"
            @input="updateSupervisor"
          />

          <v-layout row wrap>
            <v-flex sm6 xs12>
              <div class="pr-2--desktop">
                <label>{{ $t(`${namespace}.fieldCpfLabel`) }}</label>
                <v-text-field
                  v-model="form.employee.nationalId"
                  :placeholder="$t(`${namespace}.fieldCpfPlaceholder`)"
                  type="tel"
                  mask="###.###.###-##"
                  :error-messages="errors.collect($t(`${namespace}.fieldCpfName`))"
                  v-validate="{ cpf: true }"
                  :data-vv-name="$t(`${namespace}.fieldCpfName`)"
                  required
                  flat
                  outline
                  solo
                  color="accent"
                />
              </div>
            </v-flex>

            <v-flex sm6 xs12>
              <div class="pl-2--desktop">
                <label>{{ $t(`${namespace}.fieldEnrollmentLabel`) }}</label>
                <v-text-field
                  v-model="form.employee.externalId"
                  type="tel"
                  :placeholder="$t(`${namespace}.fieldEnrollmentPlaceholder`)"
                  maxlength="16"
                  mask="################"
                  flat
                  outline
                  solo
                  color="accent"
                />
              </div>
            </v-flex>
          </v-layout>
        </div>
      </fieldset>

      <fieldset
        class="form__container form__container--collapse"
        :disabled="status === isLoading || fieldsetDisabled"
      >
        <header class="form__container__header" @click="collapse('creditPolicyContainer')">
          <h2 class="form__container__title">
            {{ $t(`${namespace}.containerCreditPolicyTitle`) }}
            <v-tooltip slot="append" top min-width="200" max-width="200">
              <v-icon color="accent" small slot="activator">info</v-icon>
              <span>{{ $t(`${namespace}.containerCreditPolicyTooltip`) }}</span>
            </v-tooltip>
          </h2>
          <p class="form__container__desc">{{ $t(`${namespace}.containerCreditPolicyDesc`) }}</p>
          <v-icon :color="creditPolicyContainer ? 'accent' : 'gray-6'">
            {{ creditPolicyContainer ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
          </v-icon>
        </header>

        <div v-if="creditPolicyContainer" class="form__container__content pt-0">
          <h3 class="form__container__title">{{ $t(`${namespace}.creditPolicyContainerTabs.title`) }}</h3>

          <div class="credit-policy__tabs">
            <div
              class="credit-policy__tab__item"
              :class="[
                { 'credit-policy__tab__item--active': creditPolicyTab1 },
                { 'credit-policy__tab__item--inactive': !creditPolicyTab1 && creditPolicyTab2 },
              ]"
              @click="handlecreditPolicyTab($event, 'creditPolicyTab1', true)"
            >
              <img src="@/assets/img/controle.svg" alt="">
              <div class="credit-policy__tab__info">
                <span class="credit-policy__tab__title">{{ $t(`${namespace}.creditPolicyContainerTabs.tab1Title`) }}</span>
                <p class="credit-policy__tab__text">{{ $t(`${namespace}.creditPolicyContainerTabs.tab1Text`) }}</p>
              </div>

              <v-btn
                v-if="creditPolicyTab1"
                class="credit-policy__tab__close"
                color="accent"
                flat
                icon
                @click="handlecreditPolicyTab($event)"
              >
                <v-icon>close</v-icon>
              </v-btn>
            </div>

            <div
              class="credit-policy__tab__item"
              :class="[
                { 'credit-policy__tab__item--active': creditPolicyTab2 },
                { 'credit-policy__tab__item--inactive': !creditPolicyTab2 && creditPolicyTab1 },
              ]"
              @click="handlecreditPolicyTab($event, 'creditPolicyTab2', false)"
            >
              <img src="@/assets/img/beneflex.svg" alt="">
              <div class="credit-policy__tab__info">
                <span class="credit-policy__tab__title">{{ $t(`${namespace}.creditPolicyContainerTabs.tab2Title`) }}</span>
                <p class="credit-policy__tab__text">{{ $t(`${namespace}.creditPolicyContainerTabs.tab2Text`) }}</p>
              </div>

              <v-btn
                v-if="creditPolicyTab2"
                class="credit-policy__tab__close"
                color="accent"
                flat
                icon
                @click="handlecreditPolicyTab($event, null, false)"
              >
                <v-icon>close</v-icon>
              </v-btn>
            </div>
          </div>

          <div v-if="creditPolicyTab1 || creditPolicyTab2" class="credit-policy__tabs__content">
            <div v-if="creditPolicyTab1">
              <v-checkbox v-model="form.useBudget" class="pb-2 mt-2 mb-2" hide-details>
                <template slot="label">
                  <label class="text--semibold">{{ $t(`${namespace}.fieldBudgetValueLabel`) }}</label>
                </template>
              </v-checkbox>

              <v-layout row wrap>
                <v-flex sm6 xs12>
                  <div class="pr-2--desktop">
                    <div>
                      <label>{{ $t(`${namespace}.fieldBudgetValueLabel2`) }}</label>
                      <v-text-field
                        v-model="budgetLimitFormatted"
                        v-money="money"
                        ref="budgetLimitFormatted"
                        :placeholder="$t(`${namespace}.fieldBudgetValuePlaceholder`)"
                        :error-messages="errors.collect($t(`${namespace}.fieldBudgetValueName`))"
                        v-validate="{ required: form.useBudget }"
                        :data-vv-name="$t(`${namespace}.fieldBudgetValueName`)"
                        required
                        flat
                        outline
                        solo
                        color="accent"
                      />
                    </div>
                  </div>
                </v-flex>

                <v-flex
                  :class="form.budgetPeriod === 'date' ? 'sm3' : 'sm6'"
                  xs12
                >
                  <div class="pl-2--desktop">
                    <label>{{ $t(`${namespace}.fieldBudgetPeriodLabel`) }}</label>
                    <v-select
                      v-model="form.budgetPeriod"
                      type="number"
                      :placeholder="$t(`${namespace}.fieldBudgetPeriodPlaceholder`)"
                      item-text="label"
                      :items="objectToArray($t(`${namespace}.fieldBudgetPeriodItems`))"
                      :error-messages="errors.collect($t(`${namespace}.fieldBudgetPeriodName`))"
                      v-validate="{ required: form.useBudget }"
                      :data-vv-name="$t(`${namespace}.fieldBudgetPeriodName`)"
                      noDataText=""
                      required
                      flat
                      outline
                      solo
                      color="accent"
                    />
                  </div>
                </v-flex>

                <v-flex
                  v-if="form.budgetPeriod === 'date'"
                  :class="form.budgetPeriod === 'date' ? 'sm3' : 'sm6'"
                  xs12
                >
                  <div class="pl-4--desktop">
                    <label>{{ $t(`${namespace}.fieldBudgetPeriodLabel2`) }}</label>
                    <v-text-field
                      v-model="form.budgetDate"
                      ref="budgetDate"
                      placeholder="DD/MM/AAAA"
                      slot="activator"
                      flat
                      outline
                      solo
                      color="accent"
                      append-icon="today"
                      :name="$t(`${namespace}.fieldBudgetPeriod`)"
                      mask="##/##/####"
                      return-masked-value
                      v-validate="{
                        required: true,
                        isValidDate: true,
                        date_format: 'dd/MM/yyyy'
                      }"
                      :error-messages="errors.collect($t(`${namespace}.fieldBudgetPeriod`))"
                      :data-vv-name="$t(`${namespace}.fieldBudgetPeriod`)"
                      required
                      @click:append="budgetPeriodCalendar = true"
                    />

                    <v-dialog
                      v-model="budgetPeriodCalendar"
                      ref="calendar"
                      persistent
                      lazy
                      full-width
                      width="290"
                      class="v-dialog__container--first"
                    >
                      <v-date-picker
                        v-model="budgetDateUnformatted"
                        :locale="currentLocale"
                        :min="today"
                        color="accent"
                        @change="setBudgetDate"
                      />
                    </v-dialog>
                  </div>
                </v-flex>
              </v-layout>

              <v-checkbox
                v-model="form.useRideCredit"
                class="pb-2 mt-2"
                hide-details
                @change="creditControlFields('checkbox')"
              >
                <template slot="label">
                  <label class="text--semibold">{{ $t(`${namespace}.fieldUseRideCreditLabel`) }}</label>
                </template>
              </v-checkbox>

              <v-layout row wrap>
                <v-flex
                  :class="form.useRidePeriod && form.useRidePeriod !== 'UNTIL_REACH' ? 'sm4' : 'sm6'"
                  xs12
                >
                  <div class="pr-2--desktop">
                    <label>{{ $t(`${namespace}.fieldCreditPeriodLabel`) }}</label>
                    <v-select
                      v-model="form.useRidePeriod"
                      type="number"
                      :placeholder="$t(`${namespace}.fieldCreditPeriodPlaceholder`)"
                      item-text="label"
                      :items="objectToArray($t(`${namespace}.fieldCreditPeriodItems`))"
                      :error-messages="errors.collect($t(`${namespace}.fieldCreditPeriodName`))"
                      v-validate="{ required: form.useRideCredit }"
                      :data-vv-name="$t(`${namespace}.fieldCreditPeriodName`)"
                      noDataText=""
                      required
                      flat
                      outline
                      solo
                      color="accent"
                    />
                  </div>
                </v-flex>

                <v-flex
                  v-if="form.useRidePeriod && form.useRidePeriod !== 'UNTIL_REACH'"
                  :class="form.useRidePeriod && form.useRidePeriod !== 'UNTIL_REACH' ? 'sm4' : 'sm6'"
                  xs12
                >
                  <div class="pl-2--desktop pr-2--desktop">
                    <label>{{ $t(`${namespace}.fieldrecurringCreditLabel`) }}</label>
                    <v-text-field
                      v-model="form.recurrenceCredit"
                      type="number"
                      :placeholder="$t(`${namespace}.fieldrecurringCreditPlaceholder`)"
                      :error-messages="errors.collect($t(`${namespace}.fieldrecurringCreditName`))"
                      v-validate="{ required: form.useRideCredit, min_value: 0, max_value: 99 }"
                      :data-vv-name="$t(`${namespace}.fieldrecurringCreditName`)"
                      required
                      flat
                      outline
                      solo
                      color="accent"
                      @input="creditControlFields('input')"
                    />
                  </div>
                </v-flex>

                <v-flex
                  :class="form.useRidePeriod && form.useRidePeriod !== 'UNTIL_REACH' ? 'sm4' : 'sm6'"
                  xs12
                >
                  <div class="pl-2--desktop">
                    <label>
                      {{
                        form.useRidePeriod && form.useRidePeriod !== 'UNTIL_REACH'
                          ? $t(`${namespace}.remainingCredit`)
                          : $t(`${namespace}.fieldUseRideCreditLabel2`)
                      }}
                    </label>
                    <v-text-field
                      v-model="form.credit"
                      type="number"
                      :readonly="form.useRidePeriod && form.useRidePeriod !== 'UNTIL_REACH'"
                      :placeholder="form.useRidePeriod && form.useRidePeriod !== 'UNTIL_REACH' ? '0' : $t(`${namespace}.fieldUseRideCreditPlaceholder`)"
                      :error-messages="errors.collect($t(`${namespace}.fieldUseRideCreditName`))"
                      v-validate="{ required: (form.useRideCredit && form.useRidePeriod === 'UNTIL_REACH') || form.useRideCredit && !form.useRidePeriod, min_value: 0, max_value: 99 }"
                      :data-vv-name="$t(`${namespace}.fieldUseRideCreditName`)"
                      required
                      flat
                      outline
                      solo
                      color="accent"
                      @input="creditControlFields('input')"
                    />
                  </div>
                </v-flex>
              </v-layout>

              <transition name="fade">
                <v-alert v-show="creditAlert" type="info" outline border class="ma-0">
                  {{ $t(`${namespace}.creditAlert`) }}
                </v-alert>
              </transition>
            </div>

            <div v-if="creditPolicyTab2" class="input__tooltip__container">
              <label>{{ $t(`${namespace}.flexibleBenefitLabel`) }}</label>
              <v-text-field
                v-model="budgetLimitFormatted"
                v-money="money"
                ref="budgetLimitFormatted"
                :placeholder="$t(`${namespace}.fieldBudgetValuePlaceholder`)"
                :error-messages="errors.collect($t(`${namespace}.fieldBudgetValueName`))"
                v-validate="{ required: form.useBudget }"
                :data-vv-name="$t(`${namespace}.fieldBudgetValueName`)"
                required
                flat
                outline
                solo
                color="accent"
              />

              <v-tooltip slot="append" top min-width="200" max-width="200">
                <v-icon color="accent" slot="activator">info</v-icon>
                <span>{{ $t(`${namespace}.flexibleBenefitTooltip`) }}</span>
              </v-tooltip>
            </div>
          </div>
        </div>
      </fieldset>

      <fieldset
        class="form__container form__container--collapse"
        :disabled="status === isLoading || fieldsetDisabled"
      >
        <header class="form__container__header" @click="collapse('categoriesContainer')">
          <h2 class="form__container__title">
            {{ $t(`${namespace}.containerCategoriesTitle`) }}
            <v-tooltip slot="append" top min-width="200" max-width="200">
              <v-icon color="accent" small slot="activator">info</v-icon>
              <span>{{ $t(`${namespace}.containerCategoriesTooltip`) }}</span>
            </v-tooltip>
          </h2>
          <p class="form__container__desc">{{ $t(`${namespace}.containerCategoriesDesc`) }}</p>
          <v-icon :color="categoriesContainer ? 'accent' : 'gray-6'">
            {{ categoriesContainer ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
          </v-icon>
        </header>

        <div v-if="categoriesContainer" class="form__container__content pt-0">
          <CarCategoriesList
            :categories="categoriesList"
            form
            required
            @getCategoriesList="updateCategoriesList"
          />
        </div>
      </fieldset>

      <fieldset
        class="form__container form__container--collapse mb-0"
        :disabled="status === isLoading || fieldsetDisabled"
      >
        <header class="form__container__header" @click="collapse('platformContainer')">
          <h2 class="form__container__title">{{ $t(`${namespace}.containerPlatformAccessTitle`) }}</h2>
          <p class="form__container__desc">{{ $t(`${namespace}.containerPlatformAccessDesc`) }}</p>
          <v-icon :color="platformContainer ? 'accent' : 'gray-6'">
            {{ platformContainer ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
          </v-icon>
        </header>

        <div v-if="platformContainer" class="form__container__content pt-0">
          <div class="px-3">
            <v-checkbox v-model="form.employee.v2Permission" class="pb-2 mt-2">
              <template slot="label">
                {{ $t(`${namespace}.platformAccessCheckbox`) }}&nbsp;
                <v-tooltip min-width="200" max-width="200" top>
                  <v-icon color="accent" slot="activator" small>info</v-icon>
                  <span>{{$t(`${namespace}.platformAccessCheckboxTooltip`)}}</span>
                </v-tooltip>
              </template>
            </v-checkbox>

            <div :style="!form.employee.v2Permission && 'opacity: 0.3'">
              <p class="text-tooltip text-tooltip--info">
                <span>{{ $t(`${namespace}.platformAccessRadioButton`) }}</span>
                <v-tooltip min-width="200" max-width="200" right>
                  <v-icon color="accent" slot="activator" class="ml-1" small>info</v-icon>
                  <span>{{$t(`${namespace}.platformAccessRadioButtonTooltip`)}}</span>
                </v-tooltip>
              </p>

              <v-radio-group
                v-model="webCallPermission"
                :error-messages="errors.collect($t(`${namespace}.fieldWebCallName`))"
                :data-vv-name="$t(`${namespace}.fieldWebCallName`)"
                @change="updateWebCallPermission"
              >
                <v-radio
                  v-for="(item, index) in $t('Components.ApplyToAllDialog.setWebCallPermissionItems')"
                  :key="index"
                  :label="item.label"
                  :value="item.value"
                  :disabled="!form.employee.v2Permission"
                />
              </v-radio-group>
            </div>
          </div>
        </div>
      </fieldset>
    </form>

    <div
      v-if="status === isFailed"
      class="text-xs-center mt-5"
    >
      <span>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          href="#"
          :title="$t('Common.tryAgain')"
          class="text--link accent--text"
          @click.prevent="$emit('tryAgain')"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </span>
    </div>

    <FlexibleBenefitConfirmDialog
      :visible="flexibleBenefitConfirmDialog.visible"
      :kind="flexibleBenefitConfirmDialog.kind"
      @close="handleCloseDialog"
      @confirm="handleConfirmDialog"
    />
  </section>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { masks } from 'utils/masks'
import { VMoney } from 'v-money'
import { isObject, stringToFloat, convertToCoin, objectToArray, goBack } from 'utils/functions'
import validator from 'utils/validator'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { Omega, OmegaEventIds, OmegaBusiness, OmegaStatus } from 'common/omega'
import moment from 'moment'
import AutoComplete from 'components/AutoComplete'
import CarCategoriesList from 'components/employees/CarCategoriesList'
import FlexibleBenefitConfirmDialog from 'components/employee/FlexibleBenefitConfirmDialog'

const payload = {
  id: null, // => APENAS NA EDIÇÃO
  // new: true, // => APENAS NA CRIAÇÃO

  employee: {
    name: null,
    email: null,
    phoneCountry: 'BRA',
    phone: null, // => STRING SEM FORMATAÇÃo */
    nationalId: null, // => CPF (STRING FORMATADA)
    externalId: null, // matrícula

    // WEBCALL
    v2Permission: false,
    webCallPermission: false,
    thirdPartyWebCallPermission: false,
    categoryIds: [], // APENAS ID'S

    companyId: null, // APENAS O ID
    supervisorId: null, // APENAS O ID

    // política de uso créditos
    budgetLimit: null,

    useRidePeriod: '',

    // DEFAULT LEGADO
    usePin: false,
    pin: null // => DEFAULT "" ?
  },
  costCenters: [], // APENAS ID'S

  useRideCredit: false, // habilita caso haja valor no atributo "credit"
  credit: null, // deve conter um valor <= 99
  recurrenceCredit: 0, // deve conter um valor <= 99

  useBudget: false,
  budgetPeriod: null, // => DEFAULT 'monthly'
  budgetDate: null, // => FORMATO DD/MM/YYYY

  // flexible benefits
  budgetCumulative: false,
  budgetCurrency: null
}

export default {
  name: 'EmployeeForm',
  props: {
    kind: {
      type: String
    },
    data: {
      type: Object
    },
    submit: {
      type: Boolean
    },
    status: {
      type: Number
    }
  },
  directives: {
    money: VMoney
  },
  components: {
    AutoComplete,
    CarCategoriesList,
    FlexibleBenefitConfirmDialog
  },
  computed: {
    ...mapState({
      globalCompany: (state) => state.company.selectedCompany,
      companies: ({ session }) => session.user.business.companies,
      isAdmin: ({ session }) => session.user.business.isAdministrator
    }),

    fieldsetDisabled () {
      return !this.isValidObject(this.company) || this.costCentersFetching || this.costCentersCounter === 0
    },

    categoriesList () {
      return this.data && this.data.form && this.data.form.employee && this.data.form.employee.categoryIds
        ? this.data.form.employee.categoryIds
        /* todo: ridesCategoriesList */
        : objectToArray(this.$store.state.commonConfig.categories).map(item => item.value)
    },

    today () {
      return new Date().toJSON().slice(0, 10)
    },

    currentLocale () {
      return this.$i18n.locale.split('_').join('-')
    },

    hasMoreThanOneCompany () {
      return this.isAdmin || this.companies.length > 1
    },

    hasMoreThanOneCostCenter () {
      return this.isAdmin || this.costCentersCounter > 1
    },

    flexibleBenefit () {
      return !this.form.useBudget || this.budgetLimitFormatted === 'R$ 0,00' || this.form.budgetPeriod !== 'monthly'
    },

    creditAlert () {
      return this.form.useRidePeriod && this.form.useRidePeriod !== 'UNTIL_REACH' && this.form.credit !== this.form.recurrenceCredit
    },

    isLoading: () => STATUS_IS_LOADING,
    isSuccess: () => STATUS_SUCCESS,
    isFailed: () => STATUS_FAILED
  },
  data () {
    return {
      namespace: 'Views.EmployeePage',
      isFetching: false,
      webCallPermission: null,

      // containers
      complementInfoContainer: false,
      creditPolicyContainer: false,
      categoriesContainer: false,
      platformContainer: false,

      // datepicker
      budgetPeriodCalendar: false,
      budgetDateUnformatted: null,
      budgetDateFormatted: null,

      // cost center control
      costCentersCounter: 0,
      costCentersFetching: false,

      // custom validation
      isCompanyValid: true,
      isCostCenterValid: true,
      isSupervisorValid: true,

      // mask money config
      budgetLimitFormatted: null,
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        precision: 2,
        masked: false
      },

      // auxiliary fields
      company: null,
      costCenters: [],
      phone: null,
      supervisor: null,

      // form fields
      form: {},

      budgetLimitFormattedOld: null,
      creditPolicyTab1: false,
      creditPolicyTab2: false,
      flexibleBenefitConfirmDialog: {
        visible: false,
        kind: null
      },
      resetAllTabs: false
    }
  },
  methods: {
    ...mapActions({
      successAlert: 'snackbarAlert/success',
      errorAlert: 'snackbarAlert/error'
    }),

    isValidObject: (val) => isObject(val),

    objectToArray: (val) => objectToArray(val),

    collapse (container) {
      if (this.costCentersFetching || this.costCentersCounter === 0) return
      this[container] = !this[container]
    },

    prevPage () {
      goBack()
    },

    renderPayload () {
      const { id, ...params } = payload
      this.kind === 'editar'
        ? this.form = { id, ...params }
        : this.form = {
          new: true,
          employee: {
            name: null,
            email: null,
            phoneCountry: 'BRA',
            phone: null,
            nationalId: null,
            externalId: null,
            v2Permission: false,
            webCallPermission: false,
            thirdPartyWebCallPermission: false,
            categoryIds: [],
            companyId: null,
            supervisorId: null,
            budgetLimit: null,
            useRidePeriod: null,
            usePin: false,
            pin: null
          },
          costCenters: [],
          useRideCredit: false,
          credit: null,
          recurrenceCredit: 0,
          useBudget: false,
          budgetPeriod: null,
          budgetDate: null,
          budgetCumulative: false,
          budgetCurrency: null
        }

      this.form.employee.categoryIds = this.categoriesList
    },

    resetFields (preventReset = false, to = null, from = null) {
      if (
        preventReset ||
        (
          this.isValidObject(to) &&
          this.isValidObject(from) &&
          JSON.stringify(to) !== JSON.stringify(from)
        )
      ) {
        this.isFetching = false
        this.webCallPermission = null

        // containers
        this.complementInfoContainer = false
        this.creditPolicyContainer = false
        this.categoriesContainer = false
        this.platformContainer = false

        // datepicker
        this.budgetPeriodCalendar = false
        this.budgetDateUnformatted = null

        // cost center control
        this.costCentersCounter = 0
        this.costCentersFetching = false

        // custom validation
        this.isCompanyValid = true
        this.isCostCenterValid = true
        this.isSupervisorValid = true

        // mask
        this.budgetLimitFormatted = null

        // auxiliary fields
        this.company = null
        this.costCenters = []
        this.phone = null
        this.supervisor = null

        // form fields
        this.form = {}
        this.renderPayload()
      }
    },

    validateAutoComplete (val) {
      const isArray = Array.isArray(val) && val.length
      const isObject = val && typeof val === 'object' && Object.keys(val).length
      return Boolean(isArray || isObject)
    },

    updateCompany (val) {
      if (!val) {
        this.resetFields(true, null, null)
        return
      }

      if (JSON.stringify(val) !== JSON.stringify(this.company)) {
        if (this.validateAutoComplete(val)) {
          this.company = val
          this.form.employee.companyId = val.id
          this.isCompanyValid = true
        } else {
          this.company = null
          this.form.employee.companyId = null
          this.isCompanyValid = false
        }

        this.getCostCenters()
      }
    },

    updateCostCenter (val) {
      if (this.validateAutoComplete(val)) {
        this.costCenters = val
        this.form.costCenters = val.map(item => item.id)
        this.isCostCenterValid = true
        return
      }

      this.costCenters = []
      this.form.costCenters = []
      this.isCostCenterValid = false
    },

    updateSupervisor (val) {
      if (this.validateAutoComplete(val)) {
        this.supervisor = val
        this.form.employee.supervisorId = val.id
        this.isSupervisorValid = true
        return
      }

      this.supervisor = null
      this.form.employee.supervisorId = null
      this.isSupervisorValid = false
    },

    updateCategoriesList (val) {
      this.form.employee.categoryIds = val
    },

    updateWebCallPermission (val) {
      // reset fields
      this.form.employee.thirdPartyWebCallPermission = false
      this.form.employee.webCallPermission = false

      if (val === 'onlyForEmployee') {
        this.form.employee.webCallPermission = true
      } else if (val === 'onlyForThirdParties') {
        this.form.employee.thirdPartyWebCallPermission = true
      } else {
        this.form.employee.thirdPartyWebCallPermission = true
        this.form.employee.webCallPermission = true
      }
    },

    setPhoneMask (val, mask) {
      this.phone = masks[mask](val)
      this.form.employee.phone = val.replace(/\D/g, '')
    },

    setBudgetDate (val) {
      this.form.budgetDate = this.moment(val).format('DD/MM/YYYY')
      this.budgetDateUnformatted = this.moment(val).format('YYYY-MM-DD')
      this.budgetPeriodCalendar = false

      const input = this.$refs['budgetDate'].$el.querySelector('input')
      if (input) {
        input.focus()
        setTimeout(() => { input.blur() }, 100)
      }
    },

    setEmployeeData (val) {
      if (this.isValidObject(val)) {
        if (!val.enabled) {
          this.$router.push({ name: 'Employees' })
          return
        }

        try {
          const { form, phone, company, costCenters, supervisor } = val
          this.isFetching = true

          this.creditPolicyTab1 = false
          this.creditPolicyTab2 = false

          // containers
          if (
            (supervisor && supervisor.name) ||
            (form && form.employee && form.employee.nationalId) ||
            (form && form.employee && form.employee.externalId)
          ) {
            this.complementInfoContainer = true
          } else {
            this.complementInfoContainer = false
          }

          this.creditPolicyContainer = form.useRideCredit || form.useBudget
          this.platformContainer = form.employee.v2Permission
          this.categoriesContainer = true

          // WORK AROUND TO JIRA TASK B2B-904
          // https://99taxis.atlassian.net/browse/B2B-904
          const useBudget = form.useBudget

          // set form data
          this.form = form

          // auxiliary fields
          this.budgetLimitFormatted = convertToCoin(form.employee.budgetLimit)
          this.setPhoneMask(phone, 'phone')
          this.company = company
          this.costCenters = costCenters
          this.supervisor = supervisor

          // platform access radio group
          if (form.employee.webCallPermission && !form.employee.thirdPartyWebCallPermission) {
            this.webCallPermission = 'onlyForEmployee'
          } else if (form.employee.thirdPartyWebCallPermission && !form.employee.webCallPermission) {
            this.webCallPermission = 'onlyForThirdParties'
          } else if (form.employee.thirdPartyWebCallPermission && form.employee.webCallPermission) {
            this.webCallPermission = 'employeeAndThirdParties'
          } else {
            this.webCallPermission = null
          }

          if ((form.useBudget || form.useRideCredit) && !form.budgetCumulative) {
            this.creditPolicyTab1 = true
            this.creditPolicyTab2 = false
          }

          if (form.budgetCumulative) {
            this.creditPolicyTab1 = false
            this.creditPolicyTab2 = true
          }

          // re-render autocomplete company
          setTimeout(() => {
            this.isFetching = false

            // WORK AROUND TO JIRA TASK B2B-904
            // https://99taxis.atlassian.net/browse/B2B-904
            this.form.useBudget = useBudget
          }, 200)
        } catch (err) {
          console.log('ERROR', err)
        }
      }
    },

    async getCostCenters () {
      this.costCentersFetching = true

      try {
        const company = this.company ? this.company.id : null
        const { data: res } = await this.$apiService.getCostCentersAll(
          { limit: 5 },
          { page: 1 },
          { company }
        )

        this.costCentersCounter = res.length

        if (this.costCentersCounter === 1) {
          this.updateCostCenter(res.data)
        }

        this.costCentersFetching = false
      } catch (err) {
        console.log('ERROR', err)
      }
    },

    showErrorMessage (err) {
      let message = null

      if (
        err && err.response && err.response.data && err.response.data.errors &&
        this.isValidObject(err.response.data.errors)
      ) {
        const error = err.response.data.errors
        const key = Object.keys(error)[0]

        message = error[key][0]
      } else {
        message = this.$t(`${this.namespace}.employeeCreateErrorMessage`)
      }

      this.errorAlert(message)
    },

    setFlexibleBenefits (employeeData) {
      const emp = { ...employeeData }

      if (emp.budgetCumulative) {
        // set value
        emp.budgetCurrency = emp.employee.budgetLimit
        // reset value
        emp.employee.budgetLimit = null
      } else {
        emp.budgetCurrency = null
      }

      return emp
    },

    async createEmployee () {
      this.$emit('setCurrentStatus', this.isLoading)

      try {
        let employeeData = {
          ...this.form,
          credit: this.form.useRidePeriod !== 'UNTIL_REACH' ? this.form.recurrenceCredit : this.form.credit,
          recurrenceCredit: this.form.useRidePeriod === 'UNTIL_REACH' ? null : this.form.recurrenceCredit
        }

        // WORK AROUND => Jira task https://99taxis.atlassian.net/browse/B2B-972
        employeeData = this.setFlexibleBenefits(employeeData)

        await this.$apiService.addEmployee(employeeData)
        this.successAlert(this.$t(`${this.namespace}.employeeCreateSuccessMessage`))
        this.$emit('setCurrentStatus', this.isSuccess)
        scrollTo(0, 0)
        // this.prevPage()
        this.$router.push({ name: 'Employees' })
        this.resetFields(true, null, null)
      } catch (err) {
        console.log('ERROR', err)
        this.showErrorMessage(err)
        this.$emit('setCurrentStatus', this.isSuccess)
      }
    },

    async editEmployee () {
      this.$emit('setCurrentStatus', this.isLoading)

      try {
        let employeeData = {
          ...this.form,
          credit: this.form.useRidePeriod !== 'UNTIL_REACH' ? this.form.recurrenceCredit : this.form.credit,
          recurrenceCredit: this.form.useRidePeriod === 'UNTIL_REACH' ? null : this.form.recurrenceCredit
        }

        // remove attributes
        if (employeeData.budgetPeriod === 'monthly') delete employeeData.budgetDate

        // WORK AROUND => Jira task https://99taxis.atlassian.net/browse/B2B-972
        employeeData = this.setFlexibleBenefits(employeeData)

        await this.$apiService.editEmployee2(employeeData)
        this.successAlert(this.$t(`${this.namespace}.employeeEditsuccessMessage`))
        this.$emit('tryAgain')
        scrollTo(0, 0)
        this.$router.push({ name: 'Employees' })
      } catch (err) {
        console.log('ERROR', err)
        this.showErrorMessage(err)
        this.$emit('setCurrentStatus', this.isSuccess)
      }
    },

    async handleSubmit () {
      try {
        // 提交编辑员工
        Omega.trackBusiness(
          OmegaEventIds.tech_b2bv3_other,
          {
            business: OmegaBusiness.employee,
            type: 'submit',
            status: OmegaStatus.op
          }
        )
        const isValid = await this.$validator.validateAll()

        // autocomplete for validation
        if (!this.company) this.isCompanyValid = false
        if (!this.costCenters.length) this.isCostCenterValid = false

        if (
          isValid &&
          this.company &&
          this.costCenters.length &&
          this.form.employee.categoryIds.length
        ) {
          this.form.budgetCumulative = this.creditPolicyTab2
          this.kind === 'editar'
            ? this.editEmployee()
            : this.createEmployee()
        } else {
          const target = this.$validator.errors.items[0]
          if (target) {
            const elem = this.$el.querySelector(`[data-vv-name="${target.field}"]`)
            elem.scrollIntoView(false)
            elem.focus()
          }
        }
      } catch (err) {
        // 提交编辑员工失败
        Omega.trackBusiness(
          OmegaEventIds.tech_b2bv3_other,
          {
            business: OmegaBusiness.employee,
            type: 'submit',
            status: OmegaStatus.error,
            msg: err?.message,
            params: {
              company: {
                id: this.company?.id,
                name: this.company?.name
              },
              costCenters: this.costCenters?.map((costCenter) => {
                return {
                  id: costCenter?.id,
                  name: costCenter?.name
                }
              }),
              form: {
                employee: {
                  name: this.form?.employee?.name
                }
              },
              kind: this.kind
            }
          }
        )
        console.log('ERROR', err)
      }
    },

    creditControlFields (type) {
      if (type === 'checkbox') {
        if (!this.form.useRideCredit) {
          this.form.credit = null
          this.form.useRidePeriod = null
        }

        return
      }

      if (type === 'input') {
        this.form.useRideCredit = Boolean(this.form.credit)
      }
    },

    resetCreditPolicyFields () {
      // ride fields
      this.form.useRideCredit = false
      this.form.credit = null

      // credit fields
      this.form.useBudget = false
      this.form.employee.budgetLimit = null
      this.form.budgetPeriod = 'monthly'
      this.form.budgetDate = null
      this.budgetLimitFormatted = null

      // cumulative credit fields
      this.form.budgetCumulative = false
      this.form.budgetCurrency = null

      setTimeout(() => {
        if (this.$refs['budgetLimitFormatted']) {
          this.$refs['budgetLimitFormatted'].$el.querySelector('input').value = 'R$ 0,00'
        }
      }, 60)
    },

    resetCreditPolicyTabs () {
      this.creditPolicyTab1 = false
      this.creditPolicyTab2 = false
    },

    handleCloseDialog ({ reset, tab }) {
      this.closeFlexibleBenefitConfirmDialog()

      if (tab === 'creditPolicyTab1') {
        this.creditPolicyTab1 = true
        this.creditPolicyTab2 = false
      }

      if (tab === 'creditPolicyTab2') {
        this.creditPolicyTab1 = false
        this.creditPolicyTab2 = true
        this.form.budgetCumulative = true
      }
    },

    handleConfirmDialog ({ reset, tab }) {
      this.closeFlexibleBenefitConfirmDialog()

      if (reset) {
        this.resetCreditPolicyTabs()
        this.resetCreditPolicyFields()
      }

      if (tab === 'creditPolicyTab1') {
        this.creditPolicyTab1 = !this.resetAllTabs
        this.creditPolicyTab2 = false
        setTimeout(() => { this.resetAllTabs = false }, 1000)
      }

      if (tab === 'creditPolicyTab2') {
        this.creditPolicyTab1 = false
        this.form.budgetCumulative = true
        this.creditPolicyTab2 = true
      }
    },

    handlecreditPolicyTab (event, tab = null, reset = true) {
      const isCloseButton = event && (event.target.classList.contains('v-icon') || event.target.classList.contains('credit-policy__tab__close'))

      setTimeout(() => {
        if (this.$refs['budgetLimitFormatted']) {
          this.$refs['budgetLimitFormatted'].$el.querySelector('input').value = 'R$ 0,00'
        }
      }, 100)

      if (isCloseButton && !reset) {
        this.flexibleBenefitConfirmDialog = {
          visible: true,
          kind: 'off'
        }

        this.resetAllTabs = true

        return
      }

      if (isCloseButton && reset) {
        this.resetCreditPolicyFields()
        this.resetCreditPolicyTabs()
        return
      }

      if (tab === 'creditPolicyTab2' && !this.creditPolicyTab2) {
        this.flexibleBenefitConfirmDialog = {
          visible: true,
          kind: 'on'
        }
      }

      if (tab === 'creditPolicyTab1' && this.form.employee.budgetLimit > 0) {
        this.flexibleBenefitConfirmDialog = {
          visible: true,
          kind: 'off'
        }

        return
      }

      if (tab === 'creditPolicyTab1') {
        this.creditPolicyTab1 = true
        this.creditPolicyTab2 = false
      }

      if (tab === 'creditPolicyTab2') {
        this.form.budgetCumulative = true
      }
    },

    openFlexibleBenefitConfirmDialog (kind = 'on') {
      this.flexibleBenefitConfirmDialog = {
        visible: true,
        kind
      }
    },

    closeFlexibleBenefitConfirmDialog () {
      this.flexibleBenefitConfirmDialog = { visible: false }
    },

    convertDateToEnFormat (str) {
      return str.split('/').reverse().join('-')
    }
  },
  watch: {
    kind () {
      this.renderPayload()
    },

    submit () {
      this.handleSubmit()
    },

    company (to, from) {
      this.resetFields(false, to, from)
      this.getCostCenters()
    },

    'form.employee.budgetLimit' (val) {
      val ? this.form.useBudget = true : this.form.useBudget = false
    },

    'form.useRidePeriod' (val) {
      val ? this.form.useRideCredit = true : this.form.useRideCredit = false
    },

    'form.recurrenceCredit' (val) {
      val ? this.form.useRideCredit = true : this.form.useRideCredit = false
    },

    // TODO
    'form.useRideCredit' (val) {
      if (!val) {
        // this.form.credit = null
        // this.form.recurrenceCredit = null
      }
    },

    budgetLimitFormatted (from, to) {
      this.form.employee.budgetLimit = stringToFloat(from)
      if (!this.budgetLimitFormattedOld || (from !== to)) {
        this.budgetLimitFormattedOld = from
      }
    },

    'form.useBudget' (val) {
      if (!val && !this.form.budgetCumulative) {
        if (this.$refs['budgetLimitFormatted']) {
          this.$refs['budgetLimitFormatted'].$el.querySelector('input').value = 0
        }

        this.form.employee.budgetLimit = null
        this.form.budgetPeriod = 'monthly'
      }
    },

    'form.budgetPeriod' (val) {
      if (val === 'date' && !this.budgetDateUnformatted) {
        const date = this.form.budgetDate
          ? this.form.budgetDate.split('/').reverse().join('-')
          : this.today
        this.budgetDateUnformatted = date
        // this.form.budgetDate = moment(date).format('DD/MM/YYYY')
        return
      }

      this.budgetDateUnformatted = null
      this.form.budgetDate = null
    },

    'form.employee.v2Permission' (val) {
      if (!val) {
        this.form.employee.webCallPermission = false
        this.form.employee.thirdPartyWebCallPermission = false
        this.webCallPermission = null
      }
    },

    creditPolicyContainer (val) {
      if (!val) {
        this.form.useRideCredit = false
        this.form.credit = null
        this.form.useBudget = false
        this.form.employee.budgetLimit = null
        this.form.budgetCumulative = false
        this.form.budgetCurrency = null
        this.creditPolicyTab1 = false
        this.creditPolicyTab2 = false

        setTimeout(() => {
          if (this.$refs['budgetLimitFormatted']) {
            this.$refs['budgetLimitFormatted'].$el.querySelector('input').value = 'R$ 0,00'
          }
        }, 60)
      }
    },

    data (val) {
      this.setEmployeeData(val)
    },

    flexibleBenefit (val) {
      if (val) this.form.budgetCumulative = false
    }
  },
  created () {
    this.$validator.extend('cpf', (value) => validator.cpf(value))

    moment.locale(this.currentLocale)

    this.$validator.extend('isValidDate', {
      getMessage: field => this.$t(`${this.namespace}.invalidDate`),
      validate: value => {
        const today = moment().format('YYYY-MM-DD').valueOf()
        const date = moment(this.convertDateToEnFormat(value)).format('YYYY-MM-DD').valueOf()
        if (!value || value.length < 10 || date < today) return false
        return moment(this.convertDateToEnFormat(value))._isValid
      }
    })

    this.renderPayload()
  },
  mounted () {
    this.isFetching = true

    if (this.kind !== 'editar' && this.globalCompany) {
      this.updateCompany(this.globalCompany)
      setTimeout(() => { this.isFetching = false }, 200)
      return
    }

    this.setEmployeeData(this.data)
  }
}
</script>

<style lang="scss" scoped>
.form__container--first {
  min-height: 314px;
}

.form__group {
  min-height: 74px;
}

.form__no-label {
  display: block;
  margin: 10px 0 1px;
}

.form__no-label2 {
  display: block;
  margin-top: 3px;
}

.credit-policy__tabs {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
}

.credit-policy__tab__item {
  align-items: center;
  border: solid 1px $gray-2;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  min-height: 113px;
  margin-top: 12px;
  position: relative;
  transition: all .26s;

  @media (min-width: 1200px) {
    display: flex;
    flex-basis: 100%;
    flex: 1;
    padding-right: 12px;

    &:first-child { margin-right: 12px; }
    &:last-child { margin-left: 12px; }
  }

  img {
    margin-right: 12px;
  }

  &:hover:not(.credit-policy__tab__item--active),
  &.credit-policy__tab__item--active {
    background-color: $attention;
  }

  &:hover:not(.credit-policy__tab__item--active) {
    box-shadow: 0 3px 9px 0 rgba(#000, .14);
  }

  &.credit-policy__tab__item--active {
    border-color: $primary;
    cursor: default;
  }

  &.credit-policy__tab__item--inactive {

    img {
      filter: grayscale(100%);
    }
  }
}

.credit-policy__tab__title {
  display: flex;
  flex-direction: column;
  font-weight: 600;
}

.credit-policy__tab__text {
  display: block;
  margin-bottom: 0;
}

.credit-policy__tabs__content {
  display: block;
  padding-top: 24px;
}

.credit-policy__tab__close {
  position: absolute;
  right: 0;
  top: 2px;
  z-index: 5;
}
</style>

<style lang="scss">
.input__tooltip__container {
  position: relative;

  .v-icon {
    position: absolute;
    left: 8px;
    top: 37px;
    z-index: 1;
  }

  .v-text-field__slot {
    margin-left: 25px;
  }
}
</style>
